import { useRef, useState } from "react";
import Icon from "@/components/ui/Icon.jsx";
import { cx } from "@/lib/cx.js";

/*
 * Screenshot <img> with a skeleton, a fallback, and one automatic retry.
 *
 * Replaces frontend/app/components/ProxyImage.js. The name is kept because
 * that is what the pages call, but it no longer proxies anything — and neither
 * did the old one, in practice:
 *
 *   The old component rewrote a URL to /api/proxy-image only when the host was
 *   NOT in its allowlist (localhost, *.amazonaws.com, *.hastechit.com), while
 *   the proxy route accepted only hosts that WERE in essentially the same
 *   allowlist. The two sets barely intersect, so every URL routed to the proxy
 *   came back 403. Screenshots rendered because next/image fetched them
 *   server-side through /_next/image, not because the proxy worked.
 *
 * What actually loads the images: the API returns a freshly signed S3 URL per
 * screenshot (or a static /screenshots/… URL in local-storage mode), and a
 * plain <img> loads it directly. Cross-origin <img> needs no CORS — that only
 * applies to fetch/XHR — so there is nothing to proxy and no SSRF surface to
 * guard. Bandwidth goes browser → S3 instead of through the API, and the
 * browser's own image cache and lazy loading work normally.
 *
 * The one thing that DID need fixing is expiry: signed URLs used to last 60
 * seconds, which no one noticed while next/image was fetching them server-side
 * within that minute. Now that the browser holds them, `onExpired` lets a page
 * refetch its query to get fresh URLs when an image fails.
 */
/*
 * `fit` is a prop rather than something a caller passes through imgClassName,
 * because passing `object-contain` there does NOT win: Tailwind orders its
 * output by utility group, so whichever of object-cover/object-contain it emits
 * last applies regardless of the order in the class attribute. A thumbnail
 * wants "cover" (fill the tile, crop the edges); the full-size viewer wants
 * "contain" (show the whole screen, letterboxed) — and silently getting cover
 * there crops the top and bottom off a portrait monitor with nothing to show
 * for it.
 */
const FIT = {
    cover: "object-cover",
    contain: "object-contain",
};

export default function ProxyImage({
    src,
    alt,
    className,
    imgClassName,
    onExpired,
    fallbackLabel = "Not available",
    loading = "lazy",
    fit = "cover",
    ...rest
}) {
    // "pending" until the browser reports back, so the skeleton and the image
    // never both show. The src is kept alongside it so a new URL can reset the
    // state during render rather than in an effect — see below.
    const [attempt, setAttempt] = useState(() => ({ src, state: src ? "pending" : "failed" }));

    // One retry per URL. Without this, a genuinely missing object would ask the
    // page to refetch on every render of every thumbnail.
    const retriedFor = useRef(null);

    /*
     * A new URL is a new attempt — including the fresh URL that onExpired
     * produced. Adjusting state during render is React's documented way to react
     * to a changed prop; doing it in an effect renders the stale state first and
     * flashes the previous image's fallback.
     *
     * If the refetch hands back the SAME URL (local-storage mode, or the object
     * really is gone) nothing resets and the fallback stays put, rather than
     * spinning on a skeleton forever.
     */
    if (attempt.src !== src) {
        setAttempt({ src, state: src ? "pending" : "failed" });
    }
    const state = attempt.src === src ? attempt.state : src ? "pending" : "failed";

    const setState = (next) => setAttempt({ src, state: next });

    const handleError = () => {
        setState("failed");

        if (src && onExpired && retriedFor.current !== src) {
            retriedFor.current = src;
            onExpired();
        }
    };

    return (
        <span
            className={cx(
                "relative block overflow-hidden bg-slate-100 dark:bg-slate-800",
                className,
            )}
        >
            {state === "pending" && (
                <span className="absolute inset-0 animate-pulse bg-slate-200 dark:bg-slate-700" />
            )}

            {state === "failed" ? (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-slate-400 dark:text-slate-500">
                    <Icon name="imageOff" className="size-5" />
                    <span className="px-2 text-center text-[11px] leading-tight">
                        {fallbackLabel}
                    </span>
                </span>
            ) : (
                <img
                    src={src}
                    alt={alt}
                    loading={loading}
                    decoding="async"
                    onLoad={() => setState("loaded")}
                    onError={handleError}
                    className={cx(
                        "block size-full transition-opacity duration-200",
                        FIT[fit] ?? FIT.cover,
                        state === "loaded" ? "opacity-100" : "opacity-0",
                        imgClassName,
                    )}
                    {...rest}
                />
            )}
        </span>
    );
}
